import { useState } from 'react'
import {
  MoreVertical, RefreshCw, UserX, UserCheck, Trash2, MapPin, ChevronDown, ChevronRight,
  ArrowUp, ArrowDown, Settings2, CreditCard, SlidersHorizontal, Check, AlertCircle,
} from 'lucide-react'
import { orgAccess } from '../../admin/ui'
import { ROLE_LABELS, ROLE_COLORS, getInitials, CopyablePassword, DeleteConfirm } from './userShared'

// Notion-style table view of users for the HQ (superadmin) Users screen.
// Two layouts: a flat table with an Organisation column, or — with
// groupByOrg — collapsible per-organisation sections (the org column moves
// into the group header, alongside the org's access badge).
// Columns can be sorted (click), reordered (drag), and hidden (⚙ popover);
// the arrangement persists in localStorage.

const ORG_CHIP_COLORS = [
  'bg-teal-600', 'bg-blue-600', 'bg-violet-600', 'bg-lime-600',
  'bg-amber-600', 'bg-rose-600', 'bg-cyan-600', 'bg-indigo-600',
]

function orgChipColor(orgId) {
  let hash = 0
  for (const ch of String(orgId)) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0
  return ORG_CHIP_COLORS[hash % ORG_CHIP_COLORS.length]
}

function OrgChip({ org, dim = false }) {
  return (
    <span className={`w-5 h-5 rounded-md text-white text-[9px] font-extrabold grid place-items-center flex-none ${
      dim ? 'bg-gray-300' : orgChipColor(org.id)
    }`}>
      {getInitials(org.name)}
    </span>
  )
}

function statusOf(user) {
  if (!user.isActive) return { label: 'Inactive', text: 'text-gray-500', dot: 'bg-gray-300', rank: 2 }
  if (user.mustChangePassword) return { label: 'Password pending', text: 'text-amber-700', dot: 'bg-amber-500', rank: 1 }
  return { label: 'Active', text: 'text-teal-700', dot: 'bg-teal-500', rank: 0 }
}

const ROLE_ORDER = { superadmin: 0, admin: 1, manager: 2, staff: 3 }
const ASSIGNABLE_ROLES = ['admin', 'manager', 'staff']
const ALL_COLUMNS = ['user', 'role', 'org', 'branch', 'status', 'email', 'lastSeen', 'joined']
const DEFAULT_HIDDEN = ['email', 'lastSeen', 'joined']
const COLUMN_LABELS = {
  user: 'User', role: 'Role', org: 'Organisation', branch: 'Branch',
  status: 'Status', email: 'Email', lastSeen: 'Last seen', joined: 'Joined',
}

const fmtDate = (d) => d
  ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  : null

function loadJson(key, fallback) {
  try {
    const v = JSON.parse(localStorage.getItem(key))
    return v ?? fallback
  } catch {
    return fallback
  }
}

// Saved column state can predate newly added columns — fold unknowns out and
// missing ones in so the table never loses a column to stale localStorage.
function sanitizeCols(saved) {
  const order = (saved?.order || []).filter((k) => ALL_COLUMNS.includes(k))
  for (const k of ALL_COLUMNS) if (!order.includes(k)) order.push(k)
  const hidden = (saved?.hidden || DEFAULT_HIDDEN).filter((k) => ALL_COLUMNS.includes(k) && k !== 'user')
  return { order, hidden }
}

const MENU_HEIGHT = 230 // px, approx worst case (4 items + separator)

export default function UsersTable({
  users,
  organizations,
  branches,
  groupByOrg,
  searchActive,
  canManage,
  isSuperAdmin,
  onAssign,
  onDeactivate,
  onReactivate,
  onResetPassword,
  onDelete,
  onManageOrg,
  onSetRole,
  sort,
  onSortChange,
  clientSort = true,
  selectable = false,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}) {
  // menu: { userId, top?, bottom?, right } — fixed-position so the dropdown
  // never gets clipped by the card's horizontal-scroll wrapper.
  const [menu, setMenu] = useState(null)
  const [tempPasswords, setTempPasswords] = useState({})
  const [deletingId, setDeletingId] = useState(null)
  const [collapsed, setCollapsed] = useState({})
  const [cols, setCols] = useState(() => sanitizeCols(loadJson('pt.usersColumns', null)))
  const [colsMenu, setColsMenu] = useState(null) // { top, right }
  const [dragKey, setDragKey] = useState(null)
  const [roleMenu, setRoleMenu] = useState(null) // { userId, left, top|bottom }
  const [roleBusy, setRoleBusy] = useState(null) // userId mid-request
  const [roleError, setRoleError] = useState(null) // { userId, message }

  const roleMenuUser = roleMenu ? users.find((u) => u.id === roleMenu.userId) : null

  const menuUser = menu ? users.find((u) => u.id === menu.userId) : null
  const orgOf = (user) => organizations.find((o) => o.id === user.organizationId)
  const branchOf = (user) => branches.find((b) => b.id === user.branchId)

  function saveCols(next) {
    setCols(next)
    localStorage.setItem('pt.usersColumns', JSON.stringify(next))
  }

  // click on a header: none → asc → desc → back to default order
  function cycleSort(key) {
    if (sort?.key !== key) onSortChange({ key, dir: 'asc' })
    else if (sort.dir === 'asc') onSortChange({ key, dir: 'desc' })
    else onSortChange(null)
  }

  function moveColumn(fromKey, toKey) {
    if (!fromKey || fromKey === toKey) return
    const order = cols.order.filter((k) => k !== fromKey)
    order.splice(order.indexOf(toKey), 0, fromKey)
    saveCols({ ...cols, order })
  }

  function toggleColumn(key) {
    const hidden = cols.hidden.includes(key)
      ? cols.hidden.filter((k) => k !== key)
      : [...cols.hidden, key]
    saveCols({ ...cols, hidden })
  }

  const sortAccessors = {
    user: (u) => u.name.toLowerCase(),
    role: (u) => ROLE_ORDER[u.role],
    org: (u) => (orgOf(u)?.name || '').toLowerCase(),
    branch: (u) => (branchOf(u)?.name || '').toLowerCase(),
    status: (u) => statusOf(u).rank,
    email: (u) => (u.email || '').toLowerCase(),
    lastSeen: (u) => u.lastSeenAt?.getTime() || 0,
    joined: (u) => u.joinedAt?.getTime() || 0,
  }

  // Incoming order is the panel's default (role, then name); a null sort keeps
  // it. In server mode the query already applied the ordering, so leave it be.
  let sortedUsers = users
  if (clientSort && sort?.key && sortAccessors[sort.key]) {
    const get = sortAccessors[sort.key]
    const flip = sort.dir === 'desc' ? -1 : 1
    sortedUsers = [...users].sort((a, b) => {
      const av = get(a), bv = get(b)
      return (av < bv ? -1 : av > bv ? 1 : 0) * flip
    })
  }

  const visibleCols = (showOrg) =>
    cols.order.filter((k) => !cols.hidden.includes(k) && (showOrg || k !== 'org'))

  function openFixed(e, setter, align = 'right') {
    const rect = e.currentTarget.getBoundingClientRect()
    const openUp = rect.bottom + MENU_HEIGHT > window.innerHeight
    setter({
      ...(align === 'left'
        ? { left: rect.left }
        : { right: Math.max(8, window.innerWidth - rect.right) }),
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    })
  }

  async function handleReset(user) {
    setMenu(null)
    const result = await onResetPassword(user.id)
    if (result?.tempPassword) setTempPasswords((prev) => ({ ...prev, [user.id]: result.tempPassword }))
  }

  function menuAction(fn) {
    setMenu(null)
    fn()
  }

  async function changeRole(user, role) {
    setRoleMenu(null)
    setRoleError(null)
    if (role === user.role) return
    setRoleBusy(user.id)
    const result = await onSetRole(user.id, role)
    setRoleBusy(null)
    if (result?.success === false) {
      setRoleError({ userId: user.id, message: result.message || 'Failed to change role.' })
      return
    }
    // Managers and staff must sit at a branch; if the new role needs one and
    // the user has none, send the operator straight to the assign modal
    // rather than leaving a half-configured account behind.
    if (['manager', 'staff'].includes(role) && !user.branchId) onAssign({ ...user, role })
  }

  function Cell({ colKey, user }) {
    const org = orgOf(user)
    const branch = branchOf(user)
    const status = statusOf(user)
    const dim = !user.isActive

    switch (colKey) {
      case 'user':
        return (
          <td className="px-3.5 py-3">
            <div className="flex items-center gap-2.5">
              <span className={`w-8 h-8 rounded-full grid place-items-center text-[11px] font-bold flex-none ${
                dim ? 'bg-gray-100 text-gray-400' : 'bg-blue-100 text-blue-700'
              }`}>
                {getInitials(user.name)}
              </span>
              <div className="min-w-0">
                <p className="text-[13.5px] font-semibold text-gray-800 truncate">{user.name}</p>
                <p className="text-[11.5px] text-gray-400 truncate">
                  <span className="font-mono">@{user.username}</span>
                  {user.email ? <span className="text-gray-400"> · {user.email}</span> : null}
                </p>
              </div>
            </div>
          </td>
        )
      case 'role': {
        const editable = onSetRole && isSuperAdmin && user.role !== 'superadmin' && canManage(user)
        return (
          <td className="px-3.5 py-3">
            {editable ? (
              <button
                onClick={(e) => (roleMenu?.userId === user.id
                  ? setRoleMenu(null)
                  : openFixed(e, (pos) => setRoleMenu({ userId: user.id, ...pos }), 'left'))}
                disabled={roleBusy === user.id}
                title="Change role"
                className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold whitespace-nowrap transition hover:ring-2 hover:ring-teal-300 disabled:opacity-60 ${ROLE_COLORS[user.role]}`}
              >
                {ROLE_LABELS[user.role]}
                {roleBusy === user.id
                  ? <RefreshCw className="w-3 h-3 animate-spin" />
                  : <ChevronDown className="w-3 h-3 opacity-60" />}
              </button>
            ) : (
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${ROLE_COLORS[user.role]}`}>
                {ROLE_LABELS[user.role]}
              </span>
            )}
          </td>
        )
      }
      case 'org':
        return (
          <td className="px-3.5 py-3">
            {!org ? (
              <span className="text-gray-300">—</span>
            ) : onManageOrg ? (
              <button
                onClick={() => onManageOrg(org)}
                title={`Manage plan for ${org.name}`}
                className="group/org flex items-center gap-2 text-[13px] font-medium text-gray-700 hover:text-teal-700 transition"
              >
                <OrgChip org={org} dim={dim} />
                <span className="truncate group-hover/org:underline decoration-teal-300 underline-offset-2">{org.name}</span>
                <SlidersHorizontal className="w-3 h-3 text-teal-600 opacity-0 group-hover/org:opacity-100 transition flex-none" />
              </button>
            ) : (
              <span className="flex items-center gap-2 text-[13px] font-medium text-gray-700">
                <OrgChip org={org} dim={dim} />
                <span className="truncate">{org.name}</span>
              </span>
            )}
          </td>
        )
      case 'branch':
        return (
          <td className="px-3.5 py-3 text-[13px] text-gray-600">
            {branch ? branch.name
              : ['manager', 'staff'].includes(user.role) && user.isActive ? (
                <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold whitespace-nowrap">No branch</span>
              ) : (
                <span className="text-gray-300">—</span>
              )}
          </td>
        )
      case 'status':
        return (
          <td className="px-3.5 py-3">
            <span className={`flex items-center gap-1.5 text-[12.5px] font-semibold whitespace-nowrap ${status.text}`}>
              <span className={`w-2 h-2 rounded-full flex-none ${status.dot}`} />
              {status.label}
            </span>
          </td>
        )
      case 'email':
        return (
          <td className="px-3.5 py-3 text-[12.5px] text-gray-500">
            {user.email ? <span className="truncate block max-w-[180px]">{user.email}</span> : <span className="text-gray-300">—</span>}
          </td>
        )
      case 'lastSeen':
        return (
          <td className="px-3.5 py-3 text-[12.5px] text-gray-500 whitespace-nowrap">
            {fmtDate(user.lastSeenAt) || <span className="text-gray-300">—</span>}
          </td>
        )
      case 'joined':
        return (
          <td className="px-3.5 py-3 text-[12.5px] text-gray-500 whitespace-nowrap">
            {fmtDate(user.joinedAt) || <span className="text-gray-300">—</span>}
          </td>
        )
      default:
        return <td />
    }
  }

  function Row({ user, showOrg }) {
    const dim = !user.isActive
    const columns = visibleCols(showOrg)
    const colSpan = columns.length + 1 + (selectable ? 1 : 0)
    const manageable = canManage(user)
    const checked = !!selectedIds?.has(user.id)

    return (
      <>
        <tr className={`border-b border-gray-50 last:border-b-0 transition ${
          checked ? 'bg-teal-50/60' : 'hover:bg-gray-50/70'
        } ${dim ? 'opacity-60' : ''}`}>
          {selectable && (
            <td className="pl-3.5 pr-0 py-3">
              <input
                type="checkbox"
                aria-label={`Select ${user.name}`}
                checked={checked}
                disabled={!manageable}
                onChange={() => onToggleSelect(user.id)}
                className="w-3.5 h-3.5 accent-teal-600 disabled:opacity-30"
              />
            </td>
          )}
          {columns.map((colKey) => <Cell key={colKey} colKey={colKey} user={user} />)}
          <td className="px-3.5 py-3 text-right">
            {manageable && (
              <button
                onClick={(e) => (menu?.userId === user.id ? setMenu(null) : openFixed(e, (pos) => setMenu({ userId: user.id, ...pos })))}
                className={`w-8 h-8 inline-flex items-center justify-center rounded-lg transition ${
                  menu?.userId === user.id ? 'bg-gray-100 text-gray-600' : 'text-gray-400 hover:bg-gray-100'
                }`}
              >
                <MoreVertical className="w-4 h-4" />
              </button>
            )}
          </td>
        </tr>

        {tempPasswords[user.id] && (
          <tr className="border-b border-gray-50 last:border-b-0">
            <td colSpan={colSpan} className="px-3.5 pb-3">
              <CopyablePassword
                password={tempPasswords[user.id]}
                onDismiss={() => setTempPasswords((prev) => {
                  const next = { ...prev }
                  delete next[user.id]
                  return next
                })}
              />
            </td>
          </tr>
        )}

        {roleError?.userId === user.id && (
          <tr className="border-b border-gray-50 last:border-b-0">
            <td colSpan={colSpan} className="px-3.5 pb-3">
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                <AlertCircle className="w-4 h-4 text-red-500 flex-none mt-0.5" />
                <p className="flex-1 text-xs font-medium text-red-700">{roleError.message}</p>
                <button onClick={() => setRoleError(null)} className="text-red-400 text-xs font-bold">✕</button>
              </div>
            </td>
          </tr>
        )}

        {deletingId === user.id && (
          <tr className="border-b border-gray-50 last:border-b-0">
            <td colSpan={colSpan} className="px-3.5 pb-3">
              <DeleteConfirm user={user} onDelete={onDelete} onClose={() => setDeletingId(null)} />
            </td>
          </tr>
        )}
      </>
    )
  }

  function HeaderCell({ colKey }) {
    const active = sort?.key === colKey
    return (
      <th
        draggable
        onDragStart={() => setDragKey(colKey)}
        onDragEnd={() => setDragKey(null)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={() => moveColumn(dragKey, colKey)}
        onClick={() => cycleSort(colKey)}
        className={`text-left text-[10.5px] font-bold uppercase tracking-widest px-3.5 py-2.5 cursor-pointer select-none transition ${
          active ? 'text-teal-700' : 'text-gray-400 hover:text-gray-600'
        } ${dragKey === colKey ? 'opacity-40' : ''}`}
      >
        <span className="inline-flex items-center gap-1">
          {COLUMN_LABELS[colKey]}
          {active && (sort.dir === 'asc'
            ? <ArrowUp className="w-3 h-3" />
            : <ArrowDown className="w-3 h-3" />)}
        </span>
      </th>
    )
  }

  function Table({ rows, showOrg }) {
    const selectableRows = rows.filter((u) => canManage(u))
    const allChecked = selectableRows.length > 0 && selectableRows.every((u) => selectedIds?.has(u.id))
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {selectable && (
                <th className="pl-3.5 pr-0 py-2.5 w-8">
                  <input
                    type="checkbox"
                    aria-label="Select all on this page"
                    checked={allChecked}
                    onChange={() => onToggleSelectAll(selectableRows.map((u) => u.id), !allChecked)}
                    className="w-3.5 h-3.5 accent-teal-600"
                  />
                </th>
              )}
              {visibleCols(showOrg).map((colKey) => <HeaderCell key={colKey} colKey={colKey} />)}
              <th className="px-3.5 py-2.5 text-right">
                <button
                  aria-label="Columns"
                  onClick={(e) => (colsMenu ? setColsMenu(null) : openFixed(e, setColsMenu))}
                  className={`w-6 h-6 inline-flex items-center justify-center rounded-md transition ${
                    colsMenu ? 'bg-gray-200 text-gray-600' : 'text-gray-400 hover:bg-gray-200 hover:text-gray-600'
                  }`}
                >
                  <Settings2 className="w-3.5 h-3.5" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((user) => <Row key={user.id} user={user} showOrg={showOrg} />)}
          </tbody>
        </table>
      </div>
    )
  }

  if (users.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
        <p className="text-gray-400 text-sm">{searchActive ? 'No users match your search.' : 'No users yet.'}</p>
      </div>
    )
  }

  let body
  if (!groupByOrg) {
    body = <Table rows={sortedUsers} showOrg />
  } else {
    const groups = new Map()
    for (const user of sortedUsers) {
      const key = user.organizationId || 'none'
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(user)
    }
    const sorted = [...groups.entries()]
      .map(([key, members]) => ({ key, org: organizations.find((o) => o.id === key) || null, members }))
      .sort((a, b) => (a.org && b.org ? a.org.name.localeCompare(b.org.name) : a.org ? -1 : 1))

    body = (
      <div className="space-y-4">
        {sorted.map(({ key, org, members }) => {
          // While searching, always show matches — a collapsed group hiding
          // hits would make the search look broken.
          const isCollapsed = collapsed[key] && !searchActive
          const access = org ? orgAccess(org) : null
          return (
            <div key={key}>
              <div className="flex items-center gap-2 px-1 pb-1.5">
                <button
                  onClick={() => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }))}
                  className="flex items-center gap-2 group"
                >
                  {isCollapsed
                    ? <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                    : <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />}
                  {org && <OrgChip org={org} />}
                  <span className="text-[13.5px] font-bold text-gray-800">{org ? org.name : 'Unassigned'}</span>
                  {access && (
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${access.cls}`}>
                      {access.label}
                    </span>
                  )}
                  <span className="text-[11px] font-bold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                    {members.length} user{members.length !== 1 ? 's' : ''}
                  </span>
                </button>
                {org && onManageOrg && (
                  <button
                    onClick={() => onManageOrg(org)}
                    className="ml-1 flex items-center gap-1 text-[11px] font-semibold text-gray-400 hover:text-teal-700 transition"
                  >
                    <CreditCard className="w-3.5 h-3.5" />
                    Manage plan
                  </button>
                )}
              </div>
              {!isCollapsed && <Table rows={members} showOrg={false} />}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <>
      {body}

      {colsMenu && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setColsMenu(null)} />
          <div
            className="fixed z-40 bg-white rounded-xl shadow-xl border border-gray-100 w-48 py-2 px-1"
            style={{ right: colsMenu.right, top: colsMenu.top, bottom: colsMenu.bottom }}
          >
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-2.5 pb-1.5">Columns</p>
            {ALL_COLUMNS.filter((k) => k !== 'user').map((key) => (
              <label key={key} className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-[13px] text-gray-700 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!cols.hidden.includes(key)}
                  onChange={() => toggleColumn(key)}
                  className="w-3.5 h-3.5 accent-teal-600"
                />
                {COLUMN_LABELS[key]}
              </label>
            ))}
            <p className="text-[10.5px] text-gray-400 px-2.5 pt-1.5">Drag headers to reorder</p>
          </div>
        </>
      )}

      {roleMenuUser && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setRoleMenu(null)} />
          <div
            className="fixed z-40 bg-white rounded-xl shadow-xl border border-gray-100 w-44 py-1 overflow-hidden"
            style={{ left: roleMenu.left, right: roleMenu.right, top: roleMenu.top, bottom: roleMenu.bottom }}
          >
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3.5 py-1.5">Change role</p>
            {ASSIGNABLE_ROLES.map((role) => (
              <button
                key={role}
                onClick={() => changeRole(roleMenuUser, role)}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
              >
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${ROLE_COLORS[role]}`}>
                  {ROLE_LABELS[role]}
                </span>
                {roleMenuUser.role === role && <Check className="w-3.5 h-3.5 text-teal-600 ml-auto" />}
              </button>
            ))}
          </div>
        </>
      )}

      {menuUser && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setMenu(null)} />
          <div
            className="fixed z-40 bg-white rounded-xl shadow-xl border border-gray-100 w-48 py-1 overflow-hidden"
            style={{ right: menu.right, top: menu.top, bottom: menu.bottom }}
          >
            {menuUser.role !== 'superadmin' && (
              <button
                onClick={() => menuAction(() => onAssign(menuUser))}
                className="w-full flex items-center space-x-2.5 px-3.5 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
              >
                <MapPin className="w-4 h-4 text-teal-600" />
                <span>Assign Branch</span>
              </button>
            )}
            <button
              onClick={() => handleReset(menuUser)}
              className="w-full flex items-center space-x-2.5 px-3.5 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
            >
              <RefreshCw className="w-4 h-4 text-blue-500" />
              <span>Reset Password</span>
            </button>
            {menuUser.isActive ? (
              <button
                onClick={() => menuAction(() => onDeactivate(menuUser.id))}
                className="w-full flex items-center space-x-2.5 px-3.5 py-2.5 text-sm text-red-600 hover:bg-red-50 transition"
              >
                <UserX className="w-4 h-4" />
                <span>Deactivate</span>
              </button>
            ) : (
              <button
                onClick={() => menuAction(() => onReactivate(menuUser.id))}
                className="w-full flex items-center space-x-2.5 px-3.5 py-2.5 text-sm text-green-600 hover:bg-green-50 transition"
              >
                <UserCheck className="w-4 h-4" />
                <span>Reactivate</span>
              </button>
            )}
            {isSuperAdmin && (
              <>
                <div className="h-px bg-gray-100 mx-2 my-1" />
                <button
                  onClick={() => menuAction(() => setDeletingId(menuUser.id))}
                  className="w-full flex items-center space-x-2.5 px-3.5 py-2.5 text-sm text-red-600 hover:bg-red-50 transition"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete Account</span>
                </button>
              </>
            )}
          </div>
        </>
      )}
    </>
  )
}
